import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import CampaignSchedule from "../../../services/OrderingServices/CampaignSchedule";
import EditScheduleModal from "../../CampaignSchedule/EditScheduleModal";

const PublishModal = ({
  isOpen,
  onClose,
  campaign,
  onPublishNow,
  onSchedule,
}) => {
  const [step, setStep] = useState("options"); // options, confirmNow, schedule
  const [scheduleType, setScheduleType] = useState("once"); // once, recurring
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recurringDays, setRecurringDays] = useState({
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  });
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const validateCampaign = () => {
    const newErrors = [];

    if (!campaign.ads || campaign.ads.length === 0) {
      newErrors.push("Campaign must have at least 1 media assigned");
    }

    if (!campaign.devices || campaign.devices.length === 0) {
      newErrors.push("Campaign must have at least 1 device assigned");
    }

    return newErrors;
  };

  const handlePublishNow = () => {
    const validationErrors = validateCampaign();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setStep("confirmNow");
    setErrors([]);
  };

  const handleSchedule = () => {
    const validationErrors = validateCampaign();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsScheduleModalOpen(true);
    setErrors([]);
  };

  const handleScheduleCreate = async (scheduleId, scheduleData) => {
    try {
      // The schedule has been created via EditScheduleModal
      // Now update the campaign to mark it as scheduled
      const payload = {
        name: campaign.name,
        description: campaign.description || "",
        startDate: scheduleData.startDate,
        endDate: scheduleData.endDate,
        playUntilTurnedOff: campaign.playUntilTurnedOff,
        isDraft: false,
        isActive: false, // Will be activated by schedule
        tags: campaign.tags || [],
      };

      const response = await CampaignService.update(campaign.id, payload);
      
      // Close modals BEFORE triggering refetch to avoid race condition
      setIsScheduleModalOpen(false);
      handleClose();
      
      if (onSchedule) {
        await onSchedule(response);
      }
    } catch (error) {
      console.error("Failed to update campaign after schedule creation:", error);
    }
  };

  const handleConfirmPublishNow = async () => {
    setLoading(true);
    try {
      const payload = {
        name: campaign.name,
        description: campaign.description || "",
        startDate: new Date().toISOString(),
        endDate: campaign.endDate || null,
        playUntilTurnedOff: campaign.playUntilTurnedOff,
        isDraft: false,
        isActive: true,
        tags: campaign.tags || [],
      };

      const response = await CampaignService.update(campaign.id, payload);
      setLoading(false);

      // Close modal BEFORE triggering refetch to avoid race condition
      handleClose();

      if (onPublishNow) {
        await onPublishNow(response);
      }
    } catch (error) {
      setErrors([error.message || "Failed to publish campaign"]);
      setLoading(false);
    }
  };

  const handleConfirmSchedule = async () => {
    const newErrors = [];

    if (!startDateTime || startDateTime.length === 0) {
      newErrors.push("Start date and time is required");
    }

    if (startDateTime && endDateTime && endDateTime.length > 0) {
      if (new Date(startDateTime[0]) >= new Date(endDateTime[0])) {
        newErrors.push("End date must be after start date");
      }
    }

    if (startDateTime && new Date(startDateTime[0]) < new Date()) {
      newErrors.push("Start date must be in the future");
    }

    // Validate recurring schedule
    if (scheduleType === "recurring") {
      const hasSelectedDay = Object.values(recurringDays).some((day) => day);
      if (!hasSelectedDay) {
        newErrors.push("Please select at least one day for recurring schedule");
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: campaign.name,
        description: campaign.description || "",
        startDate: startDateTime[0].toISOString(),
        endDate:
          endDateTime && endDateTime.length > 0
            ? endDateTime[0].toISOString()
            : null,
        playUntilTurnedOff: campaign.playUntilTurnedOff,
        isDraft: false,
        isActive: false,
        tags: campaign.tags || [],
        scheduleType: scheduleType,
        ...(scheduleType === "recurring" && { recurringDays }),
      };

      const response = await CampaignService.update(campaign.id, payload);

      // Call assignToCampaigns API for recurring schedules
      if (scheduleType === "recurring") {
        // Format time as HH:mm:ss
        const formatTime = (date) => {
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${hours}:${minutes}:${seconds}`;
        };

        const scheduleData = {
          campaignIds: [campaign.id],
          startDate: startDateTime[0].toISOString(),
          endDate:
            endDateTime && endDateTime.length > 0
              ? endDateTime[0].toISOString()
              : null,
          sunday: recurringDays.sunday,
          monday: recurringDays.monday,
          tuesday: recurringDays.tuesday,
          wednesday: recurringDays.wednesday,
          thursday: recurringDays.thursday,
          friday: recurringDays.friday,
          saturday: recurringDays.saturday,
          startTime: formatTime(startDateTime[0]),
          endTime: endDateTime && endDateTime.length > 0 
            ? formatTime(endDateTime[0]) 
            : "23:59:59",
        };

        await CampaignSchedule.assignToCampaigns(scheduleData);
      }

      setLoading(false);

      // Close modal BEFORE triggering refetch to avoid race condition
      handleClose();

      if (onSchedule) {
        await onSchedule(response);
      }
    } catch (error) {
      setErrors([error.message || "Failed to schedule campaign"]);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("options");
    setScheduleType("once");
    setStartDateTime(null);
    setEndDateTime(null);
    setErrors([]);
    setRecurringDays({
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
    });
    onClose();
  };

  const handleBack = () => {
    setStep("options");
    setErrors([]);
  };

  const weekDays = [
    { key: "sunday", label: "Sun" },
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
  ];

  return (
    <>
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered>
      {/* OPTIONS STEP */}
      {step === "options" && (
        <>
          <ModalHeader toggle={handleClose}>Publish Campaign</ModalHeader>
          <ModalBody>
            <p className="text-muted mb-4">
              Choose how you'd like to publish this campaign
            </p>

            {errors.length > 0 && (
              <Alert color="danger" className="mb-3">
                <strong>Cannot Publish:</strong>
                <ul className="mt-2 mb-0">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="d-flex flex-column gap-3">
              <div
                className="border rounded p-3"
                onClick={errors.length === 0 ? handlePublishNow : null}
                style={{
                  cursor: errors.length === 0 ? "pointer" : "not-allowed",
                  opacity: errors.length === 0 ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (errors.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                    e.currentTarget.style.borderColor = "#0d6efd";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#dee2e6";
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="mt-1">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-primary"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="ms-3 flex-grow-1">
                    <div className="fw-semibold">Publish Now</div>
                    <small className="text-muted">
                      Campaign goes live immediately on all assigned devices
                    </small>
                  </div>
                </div>
              </div>

              <div
                className="border rounded p-3"
                onClick={errors.length === 0 ? handleSchedule : null}
                style={{
                  cursor: errors.length === 0 ? "pointer" : "not-allowed",
                  opacity: errors.length === 0 ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (errors.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                    e.currentTarget.style.borderColor = "#0d6efd";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#dee2e6";
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="mt-1">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-primary"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="ms-3 flex-grow-1">
                    <div className="fw-semibold">Schedule for Later</div>
                    <small className="text-muted">
                      Set a specific time or recurring schedule
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
        </>
      )}

      {/* CONFIRM NOW STEP */}
      {step === "confirmNow" && (
        <>
          <ModalHeader className="bg-warning bg-opacity-10 border-bottom">
            <div className="d-flex align-items-center">
              <span className="me-2">⚠️</span>
              <span>Confirm Publish Now</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <Alert color="info" className="mb-3">
              <strong>Important:</strong> This campaign will go live immediately
              on all assigned devices and cannot be paused.
            </Alert>

            <div className="bg-light rounded p-3">
              <div className="mb-2 d-flex justify-content-between">
                <span className="text-muted">Campaign:</span>
                <strong>{campaign.name}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span className="text-muted">Devices:</span>
                <strong>{campaign.devices?.length || 0}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span className="text-muted">Media:</span>
                <strong>{campaign.ads?.length || 0}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Go Live:</span>
                <strong>{new Date().toLocaleString()}</strong>
              </div>
            </div>

            <p className="mt-4 text-muted text-center">
              Are you sure you want to continue?
            </p>
          </ModalBody>
          <ModalFooter className="border-top">
            <Button color="light" onClick={handleBack} disabled={loading}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleConfirmPublishNow}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Publishing...
                </>
              ) : (
                "Confirm & Publish"
              )}
            </Button>
          </ModalFooter>
        </>
      )}

      {/* SCHEDULE STEP */}
      {step === "schedule" && (
        <>
          <ModalHeader toggle={handleClose}>Schedule Publication</ModalHeader>
          <ModalBody className="p-0">
            {/* Tab Navigation - Google Meet Style */}
            <Nav tabs className="border-bottom px-3 pt-3">
              <NavItem>
                <NavLink
                  className={`cursor-pointer ${
                    scheduleType === "once" ? "active" : ""
                  }`}
                  onClick={() => setScheduleType("once")}
                  style={{
                    cursor: "pointer",
                    borderBottom:
                      scheduleType === "once"
                        ? "2px solid #0d6efd"
                        : "2px solid transparent",
                    color: scheduleType === "once" ? "#0d6efd" : "#6c757d",
                    fontWeight: scheduleType === "once" ? "600" : "normal",
                  }}
                >
                  One-time Schedule
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`cursor-pointer ${
                    scheduleType === "recurring" ? "active" : ""
                  }`}
                  onClick={() => setScheduleType("recurring")}
                  style={{
                    cursor: "pointer",
                    borderBottom:
                      scheduleType === "recurring"
                        ? "2px solid #0d6efd"
                        : "2px solid transparent",
                    color: scheduleType === "recurring" ? "#0d6efd" : "#6c757d",
                    fontWeight: scheduleType === "recurring" ? "600" : "normal",
                  }}
                >
                  Recurring Schedule
                </NavLink>
              </NavItem>
            </Nav>

            <div className="p-4">
              {errors.length > 0 && (
                <Alert color="danger" className="mb-3">
                  {errors.map((error, idx) => (
                    <div key={idx}>{error}</div>
                  ))}
                </Alert>
              )}

              {/* ONE-TIME SCHEDULE */}
              {scheduleType === "once" && (
                <div>
                  <FormGroup>
                    <Label for="startDateTime" className="fw-semibold mb-2">
                      Start Date & Time <span className="text-danger">*</span>
                    </Label>
                    <Flatpickr
                      id="startDateTime"
                      value={startDateTime}
                      onChange={(date) => setStartDateTime(date)}
                      options={{
                        enableTime: true,
                        dateFormat: "Y-m-d H:i",
                        minDate: "today",
                        time_24hr: false,
                      }}
                      placeholder="Select start date and time"
                      className="form-control"
                    />
                    <small className="text-muted">
                      Campaign will go live at this time
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-4">
                    <Label for="endDateTime" className="fw-semibold mb-2">
                      End Date & Time <span className="text-muted">(Optional)</span>
                    </Label>
                    <Flatpickr
                      id="endDateTime"
                      value={endDateTime}
                      onChange={(date) => setEndDateTime(date)}
                      options={{
                        enableTime: true,
                        dateFormat: "Y-m-d H:i",
                        minDate:
                          startDateTime && startDateTime.length > 0
                            ? startDateTime[0]
                            : "today",
                        time_24hr: false,
                      }}
                      placeholder="Select end date and time"
                      className="form-control"
                    />
                    <small className="text-muted">
                      Leave empty for unlimited duration
                    </small>
                  </FormGroup>
                </div>
              )}

              {/* RECURRING SCHEDULE */}
              {scheduleType === "recurring" && (
                <div>
                  <FormGroup>
                    <Label for="startDateTime" className="fw-semibold mb-2">
                      Start Date & Time <span className="text-danger">*</span>
                    </Label>
                    <Flatpickr
                      id="startDateTime"
                      value={startDateTime}
                      onChange={(date) => setStartDateTime(date)}
                      options={{
                        enableTime: true,
                        dateFormat: "Y-m-d H:i",
                        minDate: "today",
                        time_24hr: false,
                      }}
                      placeholder="Select start date and time"
                      className="form-control"
                    />
                    <small className="text-muted">
                      Campaign will start on this date
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-4">
                    <Label className="fw-semibold mb-2">
                      Repeat On <span className="text-danger">*</span>
                    </Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {weekDays.map((day) => (
                        <Button
                          key={day.key}
                          color={
                            recurringDays[day.key]
                              ? "primary"
                              : "outline-secondary"
                          }
                          onClick={() =>
                            setRecurringDays((prev) => ({
                              ...prev,
                              [day.key]: !prev[day.key],
                            }))
                          }
                          size="sm"
                          style={{
                            minWidth: "45px",
                            fontWeight: recurringDays[day.key] ? "600" : "normal",
                          }}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                    <small className="text-muted d-block mt-2">
                      Select the days when the campaign should run
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-4">
                    <Label for="endDateTime" className="fw-semibold mb-2">
                      End Date <span className="text-muted">(Optional)</span>
                    </Label>
                    <Flatpickr
                      id="endDateTime"
                      value={endDateTime}
                      onChange={(date) => setEndDateTime(date)}
                      options={{
                        enableTime: true,
                        dateFormat: "Y-m-d H:i",
                        minDate:
                          startDateTime && startDateTime.length > 0
                            ? startDateTime[0]
                            : "today",
                        time_24hr: false,
                      }}
                      placeholder="Select end date"
                      className="form-control"
                    />
                    <small className="text-muted">
                      Recurring schedule will stop after this date
                    </small>
                  </FormGroup>

                  {/* Summary Box */}
                  {Object.values(recurringDays).some((day) => day) &&
                    startDateTime &&
                    startDateTime.length > 0 && (
                      <Alert color="info" className="mt-3">
                        <strong>Summary:</strong>
                        <div className="mt-2">
                          Campaign will run on{" "}
                          {weekDays
                            .filter((day) => recurringDays[day.key])
                            .map((day) => day.label)
                            .join(", ")}{" "}
                          starting from{" "}
                          {new Date(startDateTime[0]).toLocaleDateString()}
                          {endDateTime && endDateTime.length > 0
                            ? ` until ${new Date(
                                endDateTime[0]
                              ).toLocaleDateString()}`
                            : ""}
                        </div>
                      </Alert>
                    )}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter className="border-top">
            <Button color="light" onClick={handleBack} disabled={loading}>
              Back
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmSchedule}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Scheduling...
                </>
              ) : (
                "Confirm Schedule"
              )}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>

      {/* EditScheduleModal for creating schedules */}
      <EditScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        schedule={{
          campaignId: campaign?.id,
          campaignName: campaign?.name,
        }}
        onUpdate={handleScheduleCreate}
        isAddMode={true}
      />
    </>
  );
};

export default PublishModal;